// The custom theme's logo and favicon URLs are signed, expiring bearer links —
// an hour on cloud storage, five minutes on local disk — but the theme is
// fetched once at sign-in and held in ThemeContext for the whole session. An
// <img> that first loads one later gets the storage provider's "expired" error
// and shows a broken image: the pedigree export logo, which mounts when a
// pedigree is opened, and the Settings → Appearance previews.
//
// The handler returned here goes on such an <img>'s onError. It fetches the
// theme again, which signs fresh URLs, and swaps in ONLY logoURL and
// faviconURL: the Appearance page edits the context theme in place, so
// replacing the whole object would throw away unsaved changes. At most once a
// minute per component, so a file that is genuinely gone — every fetch signs a
// new URL, which fails again — cannot turn into a loop.

import { useCallback, useContext, useRef } from 'react';
import axios from 'src/axios';
import { APICONSTANTS } from 'src/components/Constants';
import { ThemeContext } from 'src/store/ContextStore';

export const THEME_REFRESH_MIN_INTERVAL_MS = 60 * 1000;

export default function useThemeAssetRefresh() {
  const setTheme = useContext(ThemeContext)?.setTheme;
  const lastAttempt = useRef(-Infinity);

  return useCallback(() => {
    if (!setTheme) return;
    const now = Date.now();
    if (now - lastAttempt.current < THEME_REFRESH_MIN_INTERVAL_MS) return;
    lastAttempt.current = now;
    axios
      .get(APICONSTANTS.getCustomTheme)
      .then((res) => {
        if (!res?.data) return;
        setTheme((prev) => ({
          ...prev,
          logoURL: res.data.logoURL || '',
          faviconURL: res.data.faviconURL || ''
        }));
      })
      .catch(() => {
        // Keep the theme as it is; the image stays broken rather than the
        // page failing over a logo.
      });
  }, [setTheme]);
}
